import { Injectable } from '@angular/core';
import { PerformanceMetric, MetricSummary } from '../interfaces/statistics.interface';
import { CallCenterStatsService } from './call-center-stats.service';
import { SalesStatsService } from './sales-stats.service';
import { StatsAggregationService } from './stats-aggregation.service';
import { DateService } from './date.service';
import { CallCenterStatsData } from '../interfaces/call-center-stats.interface';
import { SalesStats } from '../interfaces/sales-stats.interface';
import { MatSnackBar } from '@angular/material/snack-bar';
import { ChangeDetectorRef } from '@angular/core';

/**
 * CC metric_name → magyar megjelenítési név leképezés
 */
const CC_METRIC_DISPLAY_NAMES: { [key: string]: string } = {
  'initiated_calls': 'Indított hívás',
  'appointment_scheduled': 'Egyeztetett időpont',
  'property_insurance': 'Vagyonra egyeztetett'
};

/**
 * KÜM metric_name → magyar megjelenítési név leképezés
 */
const KUM_METRIC_DISPLAY_NAMES: { [key: string]: string } = {
  'appointment_scheduled': 'Egyeztetett időpont',
  'successful_first_meetings': 'Sikeres első tárgyalás',
  'successful_assessments': 'Sikeres felmérés',
  'successful_proposals': 'Sikeres ajánlat prezentáció',
  'successful_closures': 'Sikeres zárás'
};

@Injectable({
  providedIn: 'root'
})
export class StatisticsSummaryService {

  constructor(
    private callCenterStatsService: CallCenterStatsService,
    private salesStatsService: SalesStatsService,
    private statsAggregationService: StatsAggregationService,
    private dateService: DateService
  ) {}

  /**
   * Alapértelmezett metrikák inicializálása ha üresek
   */
  initializeDefaultMetrics(isKumMode: boolean, daysInMonth: number): PerformanceMetric[] {
    if (isKumMode) {
      // KÜM módban KÜM-specifikus metrikák
      return [
        {
          name: 'Egyeztetett időpont',
          actualValues: new Array(daysInMonth).fill(0),
          planValues: new Array(daysInMonth).fill(0),
          percentageValues: new Array(daysInMonth).fill(0),
          notes: new Array(daysInMonth).fill(null)
        },
        {
          name: 'Vagyonra egyeztetett',
          actualValues: new Array(daysInMonth).fill(0),
          planValues: new Array(daysInMonth).fill(0),
          percentageValues: new Array(daysInMonth).fill(0),
          notes: new Array(daysInMonth).fill(null)
        },
        {
          name: 'Sikeres első tárgyalás',
          actualValues: new Array(daysInMonth).fill(0),
          planValues: new Array(daysInMonth).fill(0),
          percentageValues: new Array(daysInMonth).fill(0),
          notes: new Array(daysInMonth).fill(null)
        },
        {
          name: 'Sikeres felmérés',
          actualValues: new Array(daysInMonth).fill(0),
          planValues: new Array(daysInMonth).fill(0),
          percentageValues: new Array(daysInMonth).fill(0),
          notes: new Array(daysInMonth).fill(null)
        },
        {
          name: 'Sikeres ajánlat prezentáció',
          actualValues: new Array(daysInMonth).fill(0),
          planValues: new Array(daysInMonth).fill(0),
          percentageValues: new Array(daysInMonth).fill(0),
          notes: new Array(daysInMonth).fill(null)
        },
        {
          name: 'Sikeres zárás',
          actualValues: new Array(daysInMonth).fill(0),
          planValues: new Array(daysInMonth).fill(0),
          percentageValues: new Array(daysInMonth).fill(0),
          notes: new Array(daysInMonth).fill(null)
        }
      ];
    } else {
      // Call Center módban Call Center-specifikus metrikák
      return [
        {
          name: 'Indított hívás',
          actualValues: new Array(daysInMonth).fill(0),
          planValues: new Array(daysInMonth).fill(0),
          percentageValues: new Array(daysInMonth).fill(0),
          notes: new Array(daysInMonth).fill(null)
        },
        {
          name: 'Egyeztetett időpont',
          actualValues: new Array(daysInMonth).fill(0),
          planValues: new Array(daysInMonth).fill(0),
          percentageValues: new Array(daysInMonth).fill(0),
          notes: new Array(daysInMonth).fill(null)
        },
        {
          name: 'Vagyonra egyeztetett',
          actualValues: new Array(daysInMonth).fill(0),
          planValues: new Array(daysInMonth).fill(0),
          percentageValues: new Array(daysInMonth).fill(0),
          notes: new Array(daysInMonth).fill(null)
        }
      ];
    }
  }

  /**
   * Generate summary metrics by loading and summing all colleagues' data from database
   */
  generateSummaryMetrics(
    isKumMode: boolean,
    allColleaguesWithMetrics: any[],
    daysInMonth: number,
    year: number,
    getMonthNumber: () => number,
    weekNumbers: number[],
    snackBar: MatSnackBar,
    cdr: ChangeDetectorRef,
    preloadedStatsMap?: { [userId: number]: CallCenterStatsData[] },
    preloadedKumStatsMap?: { [userId: number]: SalesStats[] }
  ): Promise<PerformanceMetric[]> {
    return new Promise((resolve, reject) => {
      if (!allColleaguesWithMetrics || allColleaguesWithMetrics.length === 0) {
        resolve([]);
        return;
      }

      if (isKumMode) {
        this.generateKumSummaryMetrics(
          allColleaguesWithMetrics,
          daysInMonth,
          year,
          getMonthNumber,
          weekNumbers,
          snackBar,
          cdr,
          preloadedKumStatsMap
        ).then(resolve).catch(reject);
      } else {
        this.generateCallCenterSummaryMetrics(
          allColleaguesWithMetrics,
          daysInMonth,
          year,
          getMonthNumber,
          weekNumbers,
          snackBar,
          cdr,
          preloadedStatsMap
        ).then(resolve).catch(reject);
      }
    });
  }

  /**
   * KÜM összesítő metrikák generálása
   */
  private generateKumSummaryMetrics(
    allColleaguesWithMetrics: any[],
    daysInMonth: number,
    year: number,
    getMonthNumber: () => number,
    weekNumbers: number[],
    snackBar: MatSnackBar,
    cdr: ChangeDetectorRef,
    preloadedKumStatsMap?: { [userId: number]: SalesStats[] }
  ): Promise<PerformanceMetric[]> {
    return new Promise((resolve, reject) => {
      // Initialize temporary accumulator object for KÜM metrics
      const summaryAccumulator = {
        'Egyeztetett időpont': {
          actualValues: new Array(daysInMonth).fill(0),
          planValues: new Array(daysInMonth).fill(0)
        },
        'Vagyonra egyeztetett': {
          actualValues: new Array(daysInMonth).fill(0),
          planValues: new Array(daysInMonth).fill(0)
        },
        'Sikeres első tárgyalás': {
          actualValues: new Array(daysInMonth).fill(0),
          planValues: new Array(daysInMonth).fill(0)
        },
        'Sikeres felmérés': {
          actualValues: new Array(daysInMonth).fill(0),
          planValues: new Array(daysInMonth).fill(0)
        },
        'Sikeres ajánlat prezentáció': {
          actualValues: new Array(daysInMonth).fill(0),
          planValues: new Array(daysInMonth).fill(0)
        },
        'Sikeres zárás': {
          actualValues: new Array(daysInMonth).fill(0),
          planValues: new Array(daysInMonth).fill(0)
        }
      };
      
      // Load stats for each KÜM colleague and sum them
      const loadPromises = allColleaguesWithMetrics.map(colleagueData => {
        return new Promise<void>((resolve) => {
          const userId = colleagueData.colleague.id
          const monthNumber = getMonthNumber();
          
          if (!userId || userId === 0 || monthNumber === -1) {
            resolve();
            return;
          }

          // Ha van preloaded adat, azt használjuk API hívás nélkül
          if (preloadedKumStatsMap && preloadedKumStatsMap[userId]) {
            const weeklyStats = preloadedKumStatsMap[userId];
            this.addKumColleagueStatsToAccumulator(weeklyStats, colleagueData.colleague.username, summaryAccumulator, daysInMonth, weekNumbers, getMonthNumber);
            resolve();
            return;
          }

          const yearToUse = this.getEffectiveYear();

          // Fallback: Load weekly sales stats for this KÜM colleague
          this.salesStatsService.getMonthStats(userId, monthNumber, yearToUse).subscribe({
            next: (weeklyStats) => {
              this.addKumColleagueStatsToAccumulator(weeklyStats, colleagueData.colleague.username, summaryAccumulator, daysInMonth, weekNumbers, getMonthNumber);
              resolve();
            },
            error: (error) => {
              resolve();
            }
          });
        });
      });

      // Wait for all colleagues data to load, then set final metrics
      Promise.all(loadPromises).then(() => {
        // Now set the final KÜM metrics from accumulator
        const metrics: PerformanceMetric[] = [
          {
            name: 'Egyeztetett időpont',
            actualValues: [...summaryAccumulator['Egyeztetett időpont'].actualValues],
            planValues: [...summaryAccumulator['Egyeztetett időpont'].planValues],
            percentageValues: new Array(daysInMonth).fill(0)
          },
          {
            name: 'Vagyonra egyeztetett',
            actualValues: [...summaryAccumulator['Vagyonra egyeztetett'].actualValues],
            planValues: [...summaryAccumulator['Vagyonra egyeztetett'].planValues],
            percentageValues: new Array(daysInMonth).fill(0)
          },
          {
            name: 'Sikeres első tárgyalás',
            actualValues: [...summaryAccumulator['Sikeres első tárgyalás'].actualValues],
            planValues: [...summaryAccumulator['Sikeres első tárgyalás'].planValues],
            percentageValues: new Array(daysInMonth).fill(0)
          },
          {
            name: 'Sikeres felmérés',
            actualValues: [...summaryAccumulator['Sikeres felmérés'].actualValues],
            planValues: [...summaryAccumulator['Sikeres felmérés'].planValues],
            percentageValues: new Array(daysInMonth).fill(0)
          },
          {
            name: 'Sikeres ajánlat prezentáció',
            actualValues: [...summaryAccumulator['Sikeres ajánlat prezentáció'].actualValues],
            planValues: [...summaryAccumulator['Sikeres ajánlat prezentáció'].planValues],
            percentageValues: new Array(daysInMonth).fill(0)
          },
          {
            name: 'Sikeres zárás',
            actualValues: [...summaryAccumulator['Sikeres zárás'].actualValues],
            planValues: [...summaryAccumulator['Sikeres zárás'].planValues],
            percentageValues: new Array(daysInMonth).fill(0)
          }
        ];
        
        this.calculateSummaryPercentages(metrics, daysInMonth, getMonthNumber);
        resolve(metrics);
      }).catch((error) => {      
        snackBar.open(
          `Hiba a KÜM kollégák adatainak betöltése során: ${error.message || error.statusText || 'Ismeretlen hiba'}`,

          'Bezárás',
          {
            duration: 6000,
            horizontalPosition: 'center',
            verticalPosition: 'top',
            panelClass: ['error-snackbar']
          }
        );
        
        reject(error);
      });
    });
  }

  /**
   * Call Center összesítő metrikák generálása
   */
  private generateCallCenterSummaryMetrics(
    allColleaguesWithMetrics: any[],
    daysInMonth: number,
    year: number,
    getMonthNumber: () => number,
    weekNumbers: number[],
    snackBar: MatSnackBar,
    cdr: ChangeDetectorRef,
    preloadedStatsMap?: { [userId: number]: CallCenterStatsData[] }
  ): Promise<PerformanceMetric[]> {
    return new Promise((resolve, reject) => {
      // Initialize temporary accumulator object for CC metrics
      const summaryAccumulator = {
        'Indított hívás': {
          actualValues: new Array(daysInMonth).fill(0),
          planValues: new Array(daysInMonth).fill(0)
        },
        'Egyeztetett időpont': {
          actualValues: new Array(daysInMonth).fill(0),
          planValues: new Array(daysInMonth).fill(0)
        },
        'Vagyonra egyeztetett': {
          actualValues: new Array(daysInMonth).fill(0),
          planValues: new Array(daysInMonth).fill(0)
        }
      };
      
      // Load stats for each colleague and sum them
      const loadPromises = allColleaguesWithMetrics.map(colleagueData => {
        return new Promise<void>((resolve) => {
          const userId = colleagueData.colleague.id;
          const monthNumber = getMonthNumber();
          
          if (!userId || userId === 0 || monthNumber === -1) {
            resolve();
            return;
          }

          // Ha van előre betöltött adat (bulk endpoint-ból), használjuk azt API hívás helyett
          if (preloadedStatsMap && preloadedStatsMap[userId]) {
            const weeklyStats = preloadedStatsMap[userId];
            this.addColleagueStatsToAccumulator(weeklyStats, colleagueData.colleague.username, summaryAccumulator, daysInMonth, weekNumbers, getMonthNumber);
            resolve();
            return;
          }

          const yearToUse = this.getEffectiveYear();

          // Load weekly stats for this colleague
          this.callCenterStatsService.getMonthStats(userId, monthNumber, yearToUse).subscribe({
            next: (weeklyStats) => {
              this.addColleagueStatsToAccumulator(weeklyStats, colleagueData.colleague.username, summaryAccumulator, daysInMonth, weekNumbers, getMonthNumber);
              resolve();
            },
            error: (error) => {
              resolve();
            }
          });
        });
      });

      // Wait for all colleagues data to load, then set final metrics
      Promise.all(loadPromises).then(() => {
        // Now set the final metrics from accumulator
        const metrics: PerformanceMetric[] = [
          {
            name: 'Indított hívás',
            actualValues: [...summaryAccumulator['Indított hívás'].actualValues],
            planValues: [...summaryAccumulator['Indított hívás'].planValues],
            percentageValues: new Array(daysInMonth).fill(0)
          },
          {
            name: 'Egyeztetett időpont',
            actualValues: [...summaryAccumulator['Egyeztetett időpont'].actualValues],
            planValues: [...summaryAccumulator['Egyeztetett időpont'].planValues],
            percentageValues: new Array(daysInMonth).fill(0)
          },
          {
            name: 'Vagyonra egyeztetett',
            actualValues: [...summaryAccumulator['Vagyonra egyeztetett'].actualValues],
            planValues: [...summaryAccumulator['Vagyonra egyeztetett'].planValues],
            percentageValues: new Array(daysInMonth).fill(0)
          }
        ];
        
        this.calculateSummaryPercentages(metrics, daysInMonth, getMonthNumber);
        resolve(metrics);
      }).catch((error) => {      
        snackBar.open(
          `Hiba a kollégák adatainak betöltése során: ${error.message || error.statusText || 'Ismeretlen hiba'}`,

          'Bezárás',
          {
            duration: 6000,
            horizontalPosition: 'center',
            verticalPosition: 'top',
            panelClass: ['error-snackbar']
          }
        );
        
        reject(error);
      });
    });
  }

  private addKumColleagueStatsToAccumulator(weeklyStats: any[], colleagueName: string, accumulator: any, daysInMonth: number, weekNumbers: number[], getMonthNumber: () => number): void {
    weeklyStats.forEach(weekData => {
      const weekNumber = weekData.week_number;
      if (!weekNumber) return;
      
      weekNumbers.forEach((dayWeekNumber, dayIndex) => {
        if (dayWeekNumber === weekNumber && dayIndex < daysInMonth) {
          const yearToUse = this.getEffectiveYear();
          
          const date = new Date(yearToUse, getMonthNumber() - 1, dayIndex + 1);
          const dayOfWeek = date.getDay();
          
          let weekDayIndex = -1;
          if (dayOfWeek >= 1 && dayOfWeek <= 5) {
            weekDayIndex = dayOfWeek - 1;
          }
          
          if (weekDayIndex >= 0 && weekDayIndex < 5) {
            
            // Egyeztetett időpont adatok hozzáadása
            if (weekData.appointment_scheduled_actual && weekDayIndex < weekData.appointment_scheduled_actual.length) {
              const value = weekData.appointment_scheduled_actual[weekDayIndex] || 0;
              accumulator['Egyeztetett időpont'].actualValues[dayIndex] += value;
            }
            if (weekData.appointment_scheduled_plan && weekDayIndex < weekData.appointment_scheduled_plan.length) {
              const value = weekData.appointment_scheduled_plan[weekDayIndex] || 0;
              accumulator['Egyeztetett időpont'].planValues[dayIndex] += value;
            }
            
            // Vagyonra egyeztetett adatok hozzáadása
            if (weekData.property_insurance_appointment_actual && weekDayIndex < weekData.property_insurance_appointment_actual.length) {
              const value = weekData.property_insurance_appointment_actual[weekDayIndex] || 0;
              accumulator['Vagyonra egyeztetett'].actualValues[dayIndex] += value;
            }
            if (weekData.property_insurance_appointment_plan && weekDayIndex < weekData.property_insurance_appointment_plan.length) {
              const value = weekData.property_insurance_appointment_plan[weekDayIndex] || 0;
              accumulator['Vagyonra egyeztetett'].planValues[dayIndex] += value;
            }
            
            // Sikeres első tárgyalás adatok hozzáadása
            if (weekData.succesfulfirstmeetings_actual && weekDayIndex < weekData.succesfulfirstmeetings_actual.length) {
              const value = weekData.succesfulfirstmeetings_actual[weekDayIndex] || 0;
              accumulator['Sikeres első tárgyalás'].actualValues[dayIndex] += value;
            }
            if (weekData.succesfulfirstmeetings_plan && weekDayIndex < weekData.succesfulfirstmeetings_plan.length) {
              const value = weekData.succesfulfirstmeetings_plan[weekDayIndex] || 0;
              accumulator['Sikeres első tárgyalás'].planValues[dayIndex] += value;
            }
            
            // Sikeres felmérés adatok hozzáadása
            if (weekData.successfulassessments_actual && weekDayIndex < weekData.successfulassessments_actual.length) {
              const value = weekData.successfulassessments_actual[weekDayIndex] || 0;
              accumulator['Sikeres felmérés'].actualValues[dayIndex] += value;
            }
            if (weekData.successfulassessments_plan && weekDayIndex < weekData.successfulassessments_plan.length) {
              const value = weekData.successfulassessments_plan[weekDayIndex] || 0;
              accumulator['Sikeres felmérés'].planValues[dayIndex] += value;
            }
            
            // Sikeres ajánlat prezentáció adatok hozzáadása
            if (weekData.successfulproposalpresentations_actual && weekDayIndex < weekData.successfulproposalpresentations_actual.length) {
              const value = weekData.successfulproposalpresentations_actual[weekDayIndex] || 0;
              accumulator['Sikeres ajánlat prezentáció'].actualValues[dayIndex] += value;
            }
            if (weekData.successfulproposalpresentations_plan && weekDayIndex < weekData.successfulproposalpresentations_plan.length) {
              const value = weekData.successfulproposalpresentations_plan[weekDayIndex] || 0;
              accumulator['Sikeres ajánlat prezentáció'].planValues[dayIndex] += value;
            }
            
            // Sikeres zárás adatok hozzáadása
            if (weekData.successfulclosures_actual && weekDayIndex < weekData.successfulclosures_actual.length) {
              const value = weekData.successfulclosures_actual[weekDayIndex] || 0;
              accumulator['Sikeres zárás'].actualValues[dayIndex] += value;
            }
            if (weekData.successfulclosures_plan && weekDayIndex < weekData.successfulclosures_plan.length) {
              const value = weekData.successfulclosures_plan[weekDayIndex] || 0;
              accumulator['Sikeres zárás'].planValues[dayIndex] += value;
            }
          }
        }
      });
    });
  }

  private addColleagueStatsToAccumulator(weeklyStats: any[], colleagueName: string, accumulator: any, daysInMonth: number, weekNumbers: number[], getMonthNumber: () => number): void {
    if (!accumulator['Indított hívás'] || !accumulator['Egyeztetett időpont']) {
      return;
    }

    weeklyStats.forEach(weekData => {
      const weekNumber = weekData.week_number;
      
      weekNumbers.forEach((dayWeekNumber, dayIndex) => {
        if (dayWeekNumber === weekNumber && dayIndex < daysInMonth) {
          const yearToUse = this.getEffectiveYear();
          
          const date = new Date(yearToUse, getMonthNumber() - 1, dayIndex + 1);
          const dayOfWeek = date.getDay();
          
          let weekDayIndex = -1;
          if (dayOfWeek >= 1 && dayOfWeek <= 5) {
            weekDayIndex = dayOfWeek - 1;
          }
          
          if (weekDayIndex >= 0 && weekDayIndex < 5) {
            // Indított hívás adatok hozzáadása az accumulator-hoz
            if (weekDayIndex < weekData.initiated_calls_actual.length) {
              const callsActual = weekData.initiated_calls_actual[weekDayIndex] || 0;
              accumulator['Indított hívás'].actualValues[dayIndex] += callsActual;
            }
            if (weekDayIndex < weekData.initiated_calls_plan.length) {
              const callsPlan = weekData.initiated_calls_plan[weekDayIndex] || 0;
              accumulator['Indított hívás'].planValues[dayIndex] += callsPlan;
            }
            
            // Egyeztetett időpont adatok hozzáadása az accumulator-hoz
            if (weekDayIndex < weekData.appointment_scheduled_actual.length) {
              const appointmentsActual = weekData.appointment_scheduled_actual[weekDayIndex] || 0;
              accumulator['Egyeztetett időpont'].actualValues[dayIndex] += appointmentsActual;
            }
            if (weekDayIndex < weekData.appointment_scheduled_plan.length) {
              const appointmentsPlan = weekData.appointment_scheduled_plan[weekDayIndex] || 0;
              accumulator['Egyeztetett időpont'].planValues[dayIndex] += appointmentsPlan;
            }
            
            // Vagyonra egyeztetett actual adatok hozzáadása
            if (weekData.property_insurance_appointment_actual && weekDayIndex < weekData.property_insurance_appointment_actual.length) {
              const propertyActual = weekData.property_insurance_appointment_actual[weekDayIndex] || 0;
              accumulator['Vagyonra egyeztetett'].actualValues[dayIndex] += propertyActual;
            }
            
            // Vagyonra egyeztetett plan adatok hozzáadása
            if (weekData.property_insurance_appointment_plan && weekDayIndex < weekData.property_insurance_appointment_plan.length) {
              const propertyPlan = weekData.property_insurance_appointment_plan[weekDayIndex] || 0;
              accumulator['Vagyonra egyeztetett'].planValues[dayIndex] += propertyPlan;
            }
          }
        }
      });
    });
  }

  /**
   * Calculate summary percentages after all data is loaded
   */
  private calculateSummaryPercentages(metrics: PerformanceMetric[], daysInMonth: number, getMonthNumber: () => number): void {
    metrics.forEach(metric => {
      for (let dayIndex = 0; dayIndex < daysInMonth; dayIndex++) {
        const actual = metric.actualValues[dayIndex];
        const plan = metric.planValues[dayIndex];
        
        // Check if it's a weekend
        const yearToUse = this.getEffectiveYear();
        const monthIndex = getMonthNumber() - 1; // Helyes hónap index a hétvége-ellenőrzéshez
        const dayOfWeek = new Date(yearToUse, monthIndex, dayIndex + 1).getDay();
        if (dayOfWeek === 0 || dayOfWeek === 6) { // 0 = Sunday, 6 = Saturday
          metric.percentageValues[dayIndex] = 0;
        } else if (plan > 0) {
          metric.percentageValues[dayIndex] = Math.round((actual / plan) * 100);
        } else {
          metric.percentageValues[dayIndex] = 0;
        }
      }
    });
  }

  private getEffectiveYear(): number {
    const currentDate = this.dateService.getCurrentDate();
    const isDebugMode = this.dateService.isDebugMode();
    return isDebugMode ? currentDate.getFullYear() : new Date().getFullYear();
  }

  // ===========================================================================
  // DB-alapú összesítő metódusok (PostgreSQL aggregáció)
  // ===========================================================================

  /**
   * DB szintű havi összesítő lekérése az összes kollégához.
   * Egyetlen API hívás + egyetlen SQL query — a korábbi N×M API hívás helyett.
   * 
   * @returns PerformanceMetric[] — a havi összesítő metrikák (plan_total, actual_total, percentage)
   *          Az actualValues/planValues/percentageValues mindegyike [total] egyelemű tömb.
   */
  generateSummaryMetricsFromDb(
    isKumMode: boolean,
    month: number,
    year: number
  ): Promise<PerformanceMetric[]> {
    return new Promise((resolve, reject) => {
      const observable = isKumMode
        ? this.statsAggregationService.getKumMonthlyAllSummary(month, year)
        : this.statsAggregationService.getCCMonthlyAllSummary(month, year);

      const displayNames = isKumMode ? KUM_METRIC_DISPLAY_NAMES : CC_METRIC_DISPLAY_NAMES;

      observable.subscribe({
        next: (dbMetrics: MetricSummary[]) => {
          const metrics: PerformanceMetric[] = dbMetrics.map(m => ({
            name: displayNames[m.metric_name] || m.metric_name,
            actualValues: [m.actual_total],
            planValues: [m.plan_total],
            percentageValues: [m.percentage]
          }));
          resolve(metrics);
        },
        error: (error) => {
          console.error('DB összesítő hiba, fallback frontend számításra:', error);
          reject(error);
        }
      });
    });
  }

  /**
   * DB szintű havi összesítő egy adott felhasználóhoz.
   * @returns PerformanceMetric[] — a felhasználó havi metrikái (plan_total, actual_total, percentage)
   */
  getUserMonthlySummaryFromDb(
    isKumMode: boolean,
    userId: number,
    month: number,
    year: number
  ): Promise<PerformanceMetric[]> {
    return new Promise((resolve, reject) => {
      const observable = isKumMode
        ? this.statsAggregationService.getKumMonthlyUserSummary(userId, month, year)
        : this.statsAggregationService.getCCMonthlyUserSummary(userId, month, year);

      const displayNames = isKumMode ? KUM_METRIC_DISPLAY_NAMES : CC_METRIC_DISPLAY_NAMES;

      observable.subscribe({
        next: (dbMetrics: MetricSummary[]) => {
          const metrics: PerformanceMetric[] = dbMetrics.map(m => ({
            name: displayNames[m.metric_name] || m.metric_name,
            actualValues: [m.actual_total],
            planValues: [m.plan_total],
            percentageValues: [m.percentage]
          }));
          resolve(metrics);
        },
        error: (error) => {
          console.error('DB user összesítő hiba:', error);
          reject(error);
        }
      });
    });
  }
}